import React from 'react';
import { Navigate } from '@tanstack/react-router';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useAuth } from '@/hooks/useAuth';
import { useTeamsAuth } from '@/hooks/useTeamsAuth';
import { isTeamsApp } from '@/utils/appType';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const styles = useStyles();
  const isTeams = isTeamsApp();

  // Always call both hooks unconditionally (required by React's Rules of Hooks)
  const webAuth = useAuth();
  const teamsAuth = useTeamsAuth();

  // Use Teams auth if in Teams, otherwise use web auth
  // Provide default values when hooks return null (not in their provider context)
  const { isAuthenticated, isLoading } = isTeams
    ? (teamsAuth ?? { isAuthenticated: false, isLoading: false })
    : webAuth;

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};